import type { OutlineHeading } from "~/utils/headingAnchorUtils";
import { escapeHtml } from "~/utils/html";
import { getScrollToTextAnchors } from "~/utils/scrollToTextAnchor";

/**
 * Builds the query string for a shared-chat URL. A `scrollToMessageId` takes priority over
 * `scrollToText` and skips the `start`/`end` text anchors entirely, since the target is looked
 * up by message id rather than matched against rendered text. `section` only labels the link (unfurl
 * title suffix) and never affects scrolling.
 */
export function buildSharePath(
  base: string,
  opts: { scrollToText?: string; scrollToMessageId?: string; section?: string | null; leaf?: string | null }
): { path: string; usedTextAnchors: boolean } {
  const params = new URLSearchParams();
  let anchors: { start: string; end: string } | null = null;
  if (opts.scrollToMessageId) {
    params.set("scrollToMessage", opts.scrollToMessageId);
  } else {
    const raw = opts.scrollToText?.trim();
    anchors = raw ? getScrollToTextAnchors(raw) : null;
    if (anchors?.start && anchors?.end) {
      params.set("start", anchors.start);
      params.set("end", anchors.end);
    }
  }
  const section = opts.section?.trim();
  if (section) {
    params.set("section", section);
  }
  if (opts.leaf) {
    params.set("leaf", opts.leaf);
  }
  const queryString = params.toString();
  const query = queryString ? `?${queryString}` : "";
  return { path: `${base}${query}`, usedTextAnchors: !!anchors };
}

/**
 * Nesting depth of each heading, 1-based, following the levels still open above it. A heading that
 * skips a level is drawn one step in rather than two — a list nested deeper than its parent renders
 * as an orphan bullet in most mail clients — and its siblings stay beside it rather than staircasing.
 */
export function outlineDepths(headings: OutlineHeading[]): number[] {
  const openLevels: number[] = [];
  return headings.map((heading) => {
    while (openLevels.length > 0 && openLevels[openLevels.length - 1] >= heading.level) openLevels.pop();
    openLevels.push(heading.level);
    return openLevels.length;
  });
}

/** Escapes a URL for an href attribute; the query separators need it as much as the quotes do. */
function escapeAttribute(url: string): string {
  return escapeHtml(url).replace(/"/g, "&quot;");
}

/**
 * Renders a shared answer's headings as a nested link list, on the clipboard's HTML and plain-text
 * halves. The title links to the share URL itself, each heading to the section it opens, so pasting
 * into an email gives the reader an outline of the answer rather than one opaque link.
 */
export function buildShareOutline(opts: {
  base: string;
  topUrl: string;
  title: string;
  headings: OutlineHeading[];
  leaf?: string | null;
}): { html: string; plainText: string } {
  const depths = outlineDepths(opts.headings);
  const rows = [{ depth: 0, text: opts.title, url: opts.topUrl }];
  opts.headings.forEach((heading, index) => {
    rows.push({
      depth: depths[index],
      text: heading.text,
      url: buildSharePath(opts.base, { scrollToText: heading.sectionText, section: heading.text, leaf: opts.leaf })
        .path,
    });
  });

  let html = "";
  let openDepth = -1;
  for (const row of rows) {
    if (row.depth > openDepth) {
      html += "<ul>".repeat(row.depth - openDepth);
    } else {
      html += "</li></ul>".repeat(openDepth - row.depth);
      html += "</li>";
    }
    html += `<li><a href="${escapeAttribute(row.url)}">${escapeHtml(row.text)}</a>`;
    openDepth = row.depth;
  }
  html += "</li></ul>".repeat(openDepth + 1);

  return {
    html,
    plainText: rows.map((row) => `${"  ".repeat(row.depth)}- [${row.text}](${row.url})`).join("\n"),
  };
}
